// `React` must be in SCOPE, not merely resolvable: the kiosk renderchecks run ts-node with
// `"jsx": "react"` (the classic runtime), which compiles this file's JSX to
// `React.createElement`. The app bundlers use the automatic runtime and need no import, so a
// missing one here builds clean and throws only in those node-only gates.
import React from "react";

import { useIsRTL } from "../../i18n";
import ChevronLeft from "./ChevronLeft";
import ChevronRight from "./ChevronRight";

/**
 * Chevrons that mean BACK and FORWARD rather than left and right.
 *
 * A back control points the way the reader came from, which in Arabic is the opposite of
 * English. `ChevronLeft` / `ChevronRight` stay literal — they are shared with the POS, where a
 * left arrow means left — and these two resolve the pair by the writing direction instead.
 *
 * 🔴 **Named for the AFFORDANCE, deliberately.** Teaching `ChevronLeft` to point right under
 * RTL would be a smaller diff and a worse idea: the name would then be a lie at every one of
 * its call sites, including the POS ones that really do want a left arrow. Ask for what you
 * mean and the mirroring is somebody else's problem.
 *
 * ⚠️ These are for HORIZONTAL affordances only. A disclosure chevron pointing up or down is
 * the same in every locale — do not reach for these to draw one (`CartScreen`'s
 * `Discounts/view` draws its own, and is marked `rtl-exempt` for exactly this reason).
 *
 * Outside a `DirectionRoot` — anywhere in the POS — `useIsRTL()` is `false`, so `ChevronBack`
 * is `ChevronLeft` and nothing changes.
 */
export function ChevronBack(props: React.ComponentProps<typeof ChevronLeft>) {
  return useIsRTL() ? <ChevronRight {...props} /> : <ChevronLeft {...props} />;
}
ChevronBack.displayName = "ChevronBack";

export function ChevronForward(props: React.ComponentProps<typeof ChevronRight>) {
  return useIsRTL() ? <ChevronLeft {...props} /> : <ChevronRight {...props} />;
}
ChevronForward.displayName = "ChevronForward";
